// The project detail page for one of the three ArcDisk demo projects.
//
// Rendered at /projects/:id (via ArcDiskProjectRouteGate) in the same frame as
// Mosaico's own project view — Layout, back link, cover Paper, header, Tabs —
// so to the viewer it is just another project. Inside the tabs the content is
// the ArcDisk class view and the raw student work, rendered by the copied
// ArcDisk components under the `.ds` token scope.
import { Anchor, Badge, Box, Button, Group, Image, Menu, Paper, Stack, Tabs, Text, Title } from '@mantine/core'
import { IconChevronLeft, IconDotsVertical, IconRotateClockwise, IconSparkles } from '@tabler/icons-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useParams } from 'react-router-dom'
import { Layout } from 'components/Layout'
import { CurrentUserContextProvider } from 'context/useCurrentUser'
import { modals, notifications } from 'context'
import { getPlaceholderImage } from 'pages/ArcProjectsList/utils'
import { ARC_AI_GRADIENT } from 'themes/ArcTheme'
import { resetProjectDemoState, useProjectDemoState } from '../data/demoState'
import { useProjectDashboard } from '../data/hooks'
import { findDemoProjectByCardId } from '../data/projects'
import { cancelAssessment, startAssessment } from '../data/simulation'
import ClassOverviewSection from './ClassOverviewSection'
import StudentWorkPanel from './StudentWorkPanel'
import '../styles/arcdisk.css'
import '../styles/arcdisk-tailwind.css'

const PROJECTS_LIST = '/arc_projects?tab=my'

const ProjectPageInner = ({ cardId, adProjectId }: { cardId: string; adProjectId: string }) => {
  const { t } = useTranslation()
  const state = useProjectDemoState(adProjectId)
  const { data } = useProjectDashboard(adProjectId)
  const [tab, setTab] = useState<string | null>('overview')

  const status = state.status
  const meta = data?.meta

  const confirmReset = () => {
    modals.openConfirmModal({
      title: t('arcdisk_demo.reset_title'),
      children: <Text size='md'>{t('arcdisk_demo.reset_body')}</Text>,
      labels: { confirm: t('arcdisk_demo.reset_confirm'), cancel: t('general_buttons_cancel') },
      confirmProps: { color: 'red', size: 'lg' },
      cancelProps: { size: 'lg' },
      onConfirm: () => {
        cancelAssessment(adProjectId)
        resetProjectDemoState(adProjectId)
        notifications.show({ message: t('arcdisk_demo.reset_done'), color: 'green' })
      },
    })
  }

  const statusBadge =
    status === 'graded' ? (
      <Badge color='violet' variant='light' size='lg' leftSection={<IconSparkles size={12} />}>
        {t('arcdisk_demo.assessed')}
      </Badge>
    ) : status === 'grading' ? (
      <Badge color='yellow' variant='light' size='lg'>
        {t('arcdisk_demo.assessing')}
      </Badge>
    ) : (
      <Badge color='gray' variant='light' size='lg'>
        {t('arcdisk_demo.not_assessed')}
      </Badge>
    )

  return (
    <>
      <Anchor component={Link} to={PROJECTS_LIST} size='md' sx={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <IconChevronLeft size={18} />
        {t('new_project_view.view_all')}
      </Anchor>
      <Paper radius='md' withBorder shadow='xs' p={0} mt='xs'>
        <Image withPlaceholder width='100%' height={240} src={getPlaceholderImage(cardId)} />
        <Box p={{ base: 'md', md: 'xl' }}>
          <Group position='apart' align='flex-start' noWrap={false}>
            <Stack spacing={6} sx={{ minWidth: 0, flex: 1 }}>
              <Title order={2}>{meta?.title ?? cardId}</Title>
              <Group spacing='xs'>
                {meta?.grade && (
                  <Badge color='gray' variant='outline' size='lg'>
                    {t('arcdisk_demo.grade')} {meta.grade}
                  </Badge>
                )}
                {meta?.subjects?.map(s => (
                  <Badge key={s} color='gray' variant='outline' size='lg'>
                    {s}
                  </Badge>
                ))}
                {meta && (
                  <Badge color='gray' variant='outline' size='lg'>
                    {meta.students_submitted} {t('arcdisk_demo.students')}
                  </Badge>
                )}
                {statusBadge}
              </Group>
            </Stack>
            <Group spacing='sm' noWrap>
              <Button
                size='md'
                radius='xl'
                leftIcon={<IconSparkles size={18} />}
                onClick={() => startAssessment(adProjectId)}
                disabled={status !== 'ungraded'}
                loading={status === 'grading'}
                styles={{
                  root: {
                    background: ARC_AI_GRADIENT,
                    border: 0,
                    '&:not([data-disabled]):hover': { filter: 'brightness(0.95)' },
                  },
                }}
              >
                {status === 'grading' ? t('arcdisk_demo.assessing') : t('arcdisk_demo.assess')}
              </Button>
              <Menu shadow='md' width={220} position='bottom-end'>
                <Menu.Target>
                  <Button variant='default' size='md' radius='xl' px='xs' aria-label={t('arc_projects.actions')}>
                    <IconDotsVertical size={18} />
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    icon={<IconRotateClockwise size={16} />}
                    color='red'
                    disabled={status === 'ungraded'}
                    onClick={confirmReset}
                  >
                    {t('arcdisk_demo.reset')}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>


          <Tabs mt='lg' value={tab} onTabChange={setTab} keepMounted={false}>
            <Tabs.List>
              <Tabs.Tab value='overview'>
                <Text size='md'>{t('arcdisk_demo.tab_overview')}</Text>
              </Tabs.Tab>
              <Tabs.Tab value='work'>
                <Text size='md'>{t('arcdisk_demo.tab_work')}</Text>
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value='overview' pt='md'>
              <div className='ds arcdisk-root'>
                <ClassOverviewSection adProjectId={adProjectId} cardId={cardId} />
              </div>
            </Tabs.Panel>
            <Tabs.Panel value='work' pt='md'>
              <div className='ds arcdisk-root'>
                <StudentWorkPanel adProjectId={adProjectId} cardId={cardId} />
              </div>
            </Tabs.Panel>
          </Tabs>
        </Box>
      </Paper>
    </>
  )
}

export const ArcDiskProjectPage = () => {
  const { id } = useParams()
  const ref = findDemoProjectByCardId(id)
  if (!ref) return <Navigate to={PROJECTS_LIST} replace />
  return (
    <CurrentUserContextProvider>
      <Layout>
        <ProjectPageInner cardId={ref.cardId} adProjectId={ref.adProjectId} />
      </Layout>
    </CurrentUserContextProvider>
  )
}
